import {Theme} from './types';

const CONTRAST_RATIO_THRESHOLD = 3;
const LIGHT_BACKGROUND_RGBA = [0, 0, 0, 0.03];
const DARK_BACKGROUND_RGBA = [255, 255, 255, 0.03];
const PURPLE_LOGO_RGB = [90, 49, 244];
const WHITE_LOGO_RGB = [255, 255, 255];
const FONT_LUMINANCE_THRESHOLD = 0.4;

/**
 * The current algorithm for luminance can be found in the WCAG 2.0 documentation.
 * More specifically, the breakdown of the formula is the detailed in the first step of
 * the algorithm detailed in: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-procedure
 *
 * @param {number} red color.
 * @param {number} green color.
 * @param {number} blue color.
 * @returns {Array<number>} luminance.
 */
export function luminance(rgb: number[]): number {
  const luminancePerColor = rgb.map(function (color) {
    const colorRatio = color / 255;

    return colorRatio <= 0.03928
      ? colorRatio / 12.92
      : ((colorRatio + 0.055) / 1.055) ** 2.4;
  });

  return (
    luminancePerColor[0] * 0.2126 +
    luminancePerColor[1] * 0.7152 +
    luminancePerColor[2] * 0.0722
  );
}

/**
 * @param {Array<number>} rgb1 color.
 * @param {Array<number>} rgb2 color.
 * @returns {number} contrast between two colors
 */
export function contrast(rgb1: number[], rgb2: number[]): number {
  const lum1 = luminance(rgb1);
  const lum2 = luminance(rgb2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
}

export function getNearestParentElementBackground(initialElement: HTMLElement) {
  let currentElement: HTMLElement | null = initialElement;
  const transparent = 'rgba(0, 0, 0, 0)';
  let parentBackgroundColor = '#FFFFFF';

  while (currentElement) {
    const currentElementBackgroundColor =
      getComputedStyle(currentElement).backgroundColor;

    if (currentElementBackgroundColor === transparent) {
      currentElement = currentElement.parentElement;
    } else {
      parentBackgroundColor = currentElementBackgroundColor;
      break;
    }
  }
  return parentBackgroundColor;
}

/**
 * @param {string} backgroundColor hex or rgb string for the background.
 * @returns {Theme} LIGHT or DARK
 */
export function pickThemeColor(backgroundColor: string): Theme {
  // Default to white background
  let backgroundColorRgb = WHITE_LOGO_RGB;

  if (backgroundColor.startsWith('#')) {
    backgroundColorRgb = hexToRGB(backgroundColor);
  } else if (backgroundColor.startsWith('rgb')) {
    backgroundColorRgb = rgbaStringToArr(backgroundColor);
  }

  return contrast(backgroundColorRgb, PURPLE_LOGO_RGB) >
    contrast(backgroundColorRgb, WHITE_LOGO_RGB)
    ? Theme.Light
    : Theme.Dark;
}

/**
 * Converts HEX color string into RGB array.
 *
 * @param {string} hex string e.g. #FFF or #CAFEFE
 * @returns {Array<number>} like [255, 255, 255] or [202, 254, 254]
 */
export function hexToRGB(hex: string): number[] {
  let red = 0;
  let green = 0;
  let blue = 0;

  // 3 digits
  if (hex.length === 4) {
    red = Number(`0x${hex[1]}${hex[1]}`);
    green = Number(`0x${hex[2]}${hex[2]}`);
    blue = Number(`0x${hex[3]}${hex[3]}`);

    // 6 digits
  } else if (hex.length === 7) {
    red = Number(`0x${hex[1]}${hex[2]}`);
    green = Number(`0x${hex[3]}${hex[4]}`);
    blue = Number(`0x${hex[5]}${hex[6]}`);
  }

  return [red, green, blue];
}

/**
 * Converts RGBA array into RGB array while accounting for the background
 * see https://stackoverflow.com/questions/21576092/convert-rgba-to-hex/21576659#21576659
 */
export function rgbaToRGB(rgba: number[], rgbBackground: number[]) {
  const [red, green, blue, alpha] = rgba;
  const [redBackground, greenBackground, blueBackground] = rgbBackground;

  return [
    Math.round((1 - alpha) * redBackground + alpha * red),
    Math.round((1 - alpha) * greenBackground + alpha * green),
    Math.round((1 - alpha) * blueBackground + alpha * blue),
  ];
}

/**
 * Converts RGB or RGBA color string into RGB or RGBA array.
 *
 * @param {string} rgbaString rgba(255,255,255, 1) or rgba(0 0 0 1) or rba(255, 255, 255)
 * @returns {Array<number>} [255, 255, 255, 1] or [0, 0, 0, 1] or [255, 255, 255]
 */
export function rgbaStringToArr(rgba: string): number[] {
  const rgbaValues = rgba.match(/(\d+\.\d+|\d+)/g) || [];
  const rgbaArr = rgbaValues.map(Number);

  return rgbaArr;
}

export function getModuleBackgroundColor(
  parentBackgroundColor: string,
  theme: Theme,
) {
  const backgroundColorArr = rgbaStringToArr(parentBackgroundColor);
  if (backgroundColorArr.length === 4) {
    const moduleBackgroundColor =
      theme === Theme.Light ? LIGHT_BACKGROUND_RGBA : DARK_BACKGROUND_RGBA;

    return rgbaToRGB(backgroundColorArr, moduleBackgroundColor);
  }

  return backgroundColorArr;
}

export function getModuleFontColor(fontColor: string, backgroundColor: string) {
  const fontColorArr = rgbaStringToArr(fontColor);

  if (fontColorArr.length === 4) {
    return rgbaToRGB(fontColorArr, rgbaStringToArr(backgroundColor));
  }

  return fontColorArr;
}

export function hasSufficientContrast(
  contrastRatio: number,
  threshold: number = CONTRAST_RATIO_THRESHOLD,
) {
  return contrastRatio >= threshold;
}

export function hasSufficientLuminance(
  fontColor: number[],
  theme: Theme,
  threshold: number = FONT_LUMINANCE_THRESHOLD,
) {
  const logoColor = theme === Theme.Light ? PURPLE_LOGO_RGB : WHITE_LOGO_RGB;
  const luminances = [luminance(fontColor), luminance(logoColor)];

  return Math.max(...luminances) - Math.min(...luminances) < threshold;
}

export function shouldOverrideFont(
  fontColor: number[],
  backgroundColor: number[],
  theme: Theme,
) {
  return (
    !hasSufficientContrast(contrast(fontColor, backgroundColor)) ||
    !hasSufficientLuminance(fontColor, theme)
  );
}
